import { format, formatDistanceToNow } from 'date-fns';
import {
  UserDisplayPreferences,
  DEFAULT_DISPLAY_PREFERENCES,
  DateFormat,
  TimeFormat,
} from '../types/userPreferences';

/**
 * Recursively removes undefined properties from an object or array for Firestore compatibility.
 * Preserves Date, Timestamp, FieldValue, and other custom class instances.
 */
export const removeUndefined = <T>(obj: T): T => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  // Preserve Date, Timestamp, FieldValue, or other class instances
  if (
    obj instanceof Date ||
    (obj.constructor && obj.constructor.name !== 'Object' && !Array.isArray(obj))
  ) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefined) as unknown as T;
  }

  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = typeof value === 'object' && value !== null ? removeUndefined(value) : value;
    }
  }
  return cleaned as T;
};

/**
 * Safely converts various timestamp representation types (Firestore Timestamp, Date, number, string) to epoch milliseconds.
 */
export const getTimestampMillis = (v: any, fallback?: number): number => {
  if (v === null || v === undefined) return fallback !== undefined ? fallback : Date.now();
  if (typeof v === 'number') return v;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') {
    const parsed = Date.parse(v);
    return isNaN(parsed) ? (fallback !== undefined ? fallback : Date.now()) : parsed;
  }
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  if (typeof v?._seconds === 'number') return v._seconds * 1000;
  return fallback !== undefined ? fallback : Date.now();
};

/**
 * Safely converts optional timestamp fields to epoch milliseconds or undefined.
 */
export const getOptionalTimestampMillis = (v: any): number | undefined => {
  if (v === null || v === undefined) return undefined;
  return getTimestampMillis(v);
};

/**
 * Get date-fns format string based on user's date format preference.
 */
function getDateFormatString(dateFormat: DateFormat, includeWeekday: boolean = false): string {
  
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return includeWeekday ? 'EEEE, MMMM d, yyyy' : 'MMM d, yyyy';
    case 'DD/MM/YYYY':
      return includeWeekday ? 'EEEE, d MMMM yyyy' : 'd MMM yyyy';
    case 'YYYY-MM-DD':
      return includeWeekday ? 'EEEE, yyyy-MM-dd' : 'yyyy-MM-dd';
    default:
      return includeWeekday ? 'EEEE, MMMM d, yyyy' : 'MMM d, yyyy';
  }
}

/**
 * Get date-fns format string for short date display (without year).
 */
function getShortDateFormatString(dateFormat: DateFormat, includeWeekday: boolean = false): string {
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return includeWeekday ? 'EEEE, MMMM d' : 'MMM d';
    case 'DD/MM/YYYY':
      return includeWeekday ? 'EEEE, d MMMM' : 'd MMM';
    case 'YYYY-MM-DD':
      return includeWeekday ? 'EEEE, MM-dd' : 'MM-dd';
    default:
      return includeWeekday ? 'EEEE, MMMM d' : 'MMM d';
  }
}

/**
 * Get date-fns format string for full month name display.
 */
function getFullDateFormatString(dateFormat: DateFormat): string {
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return 'MMMM d, yyyy';
    case 'DD/MM/YYYY':
      return 'd MMMM yyyy';
    case 'YYYY-MM-DD':
      return 'yyyy-MM-dd';
    default:
      return 'MMMM d, yyyy';
  }
}

/**
 * Get date-fns format string for time based on user preference.
 */
function getTimeFormatString(timeFormat: TimeFormat): string {
  return timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
}

/**
 * Format a date according to user preferences.
 * @param date - Date to format (Date object or date string)
 * @param preferences - User display preferences (uses defaults if not provided)
 * @param options - Formatting options
 */
export function formatDate(
  date: Date | string,
  preferences?: Partial<UserDisplayPreferences> | null,
  options: {
    includeWeekday?: boolean;
    shortFormat?: boolean;
    fullMonth?: boolean;
  } = {}
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  let formatString: string;
  
  if (options.fullMonth) {
    formatString = getFullDateFormatString(prefs.dateFormat);
    if (options.includeWeekday) {
      formatString = 'EEEE, ' + formatString;
    }
  } else if (options.shortFormat) {
    formatString = getShortDateFormatString(prefs.dateFormat, options.includeWeekday);
  } else {
    formatString = getDateFormatString(prefs.dateFormat, options.includeWeekday);
  }

  return format(dateObj, formatString);
}

/**
 * Format a time according to user preferences.
 * @param date - Date to format (Date object or date string)
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatTime(
  date: Date | string,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return format(dateObj, getTimeFormatString(prefs.timeFormat));
}

/**
 * Format a date and time according to user preferences.
 * @param date - Date to format (Date object or date string)
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatDateTime(
  date: Date | string,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const dateFormatString = getDateFormatString(prefs.dateFormat, false);
  const timeFormatString = getTimeFormatString(prefs.timeFormat);
  
  return format(dateObj, `${dateFormatString} ${timeFormatString}`);
}

/**
 * Format relative time (e.g., "2 hours ago").
 * This is not affected by user preferences.
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Parse a time string and convert it to user's preferred format.
 * Handles various time formats: "8:00 AM", "14:30", "8:00", "8:00am", etc.
 * @param timeString - The time string to format
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatTimeString(
  timeString: string | undefined | null,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  if (!timeString) return '';
  
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  
  // Try to parse the time string
  const trimmed = timeString.trim();
  
  // Match patterns like "8:00 AM", "8:00am", "14:30", "8:00"
  const timeRegex = /^(\d{1,2}):(\d{2})\s*(am|pm)?$/i;
  const match = trimmed.match(timeRegex);
  
  if (!match) {
    // Can't parse, return original
    return timeString;
  }
  
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3]?.toLowerCase();
  
  // Convert to 24-hour format first
  if (period === 'pm' && hours !== 12) {
    hours += 12;
  } else if (period === 'am' && hours === 12) {
    hours = 0;
  }
  // If no period specified and hours <= 12, assume it's already in correct format
  // (could be 24h format if > 12)
  
  // Now format according to preference
  if (prefs.timeFormat === '24h') {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } else {
    // 12-hour format
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayPeriod = hours >= 12 ? 'PM' : 'AM';
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${displayPeriod}`;
  }
}

/**
 * Format currency according to user preferences.
 * Note: This only changes the display format/symbol, not the actual currency conversion.
 * @param amount - Numeric amount to format
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatCurrency(
  amount: number | undefined | null,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  
  if (amount === undefined || amount === null) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: prefs.currency,
    }).format(0);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: prefs.currency,
  }).format(amount);
}

/**
 * Conversion factors for imperial to metric.
 */
const CONVERSION_FACTORS = {
  feet_to_meters: 0.3048,
  pounds_to_kg: 0.453592,
  miles_to_km: 1.60934,
  knots_to_kmh: 1.852,
};

/**
 * Format a length measurement according to user preferences.
 * @param value - Value in imperial units (feet)
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatLength(
  value: string | number | undefined | null,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return String(value);
  }

  if (prefs.units === 'metric') {
    const meters = numericValue * CONVERSION_FACTORS.feet_to_meters;
    return `${meters.toLocaleString('en-US', { maximumFractionDigits: 1 })} m`;
  }

  return `${numericValue.toLocaleString('en-US')} ft`;
}

/**
 * Format ship dimensions (length × width) according to user preferences.
 * @param length - Ship length in feet
 * @param width - Ship width in feet
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatDimensions(
  length: string | number | undefined | null,
  width: string | number | undefined | null,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  
  if (!length && !width) {
    return '';
  }

  const parseValue = (val: string | number | undefined | null): number | null => {
    if (val === undefined || val === null || val === '') return null;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? null : num;
  };

  const lengthNum = parseValue(length);
  const widthNum = parseValue(width);

  if (lengthNum && widthNum) {
    if (prefs.units === 'metric') {
      const lengthM = (lengthNum * CONVERSION_FACTORS.feet_to_meters).toLocaleString('en-US', { maximumFractionDigits: 1 });
      const widthM = (widthNum * CONVERSION_FACTORS.feet_to_meters).toLocaleString('en-US', { maximumFractionDigits: 1 });
      return `${lengthM} × ${widthM} m`;
    }
    return `${lengthNum.toLocaleString('en-US')} × ${widthNum.toLocaleString('en-US')} ft`;
  }

  if (lengthNum) {
    return formatLength(lengthNum, prefs);
  }

  if (widthNum) {
    return formatLength(widthNum, prefs);
  }

  return '';
}

/**
 * Format tonnage (gross registered tonnage).
 * Tonnage is a volume measurement, not affected by imperial/metric preference.
 * @param value - Tonnage value
 */
export function formatTonnage(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return String(value);
  }

  return `${numericValue.toLocaleString('en-US')} GT`;
}

/**
 * Format speed (knots) according to user preferences.
 * @param value - Speed in knots
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function formatSpeed(
  value: string | number | undefined | null,
  preferences?: Partial<UserDisplayPreferences> | null
): string {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return String(value);
  }

  if (prefs.units === 'metric') {
    const kmh = numericValue * CONVERSION_FACTORS.knots_to_kmh;
    return `${kmh.toLocaleString('en-US', { maximumFractionDigits: 1 })} km/h`;
  }

  return `${numericValue.toLocaleString('en-US')} knots`;
}

/**
 * Format a number with thousands separators.
 * Used for counts like passengers, crew, cabins, decks.
 * @param value - Number to format
 */
export function formatNumber(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return String(value);
  }

  return numericValue.toLocaleString('en-US');
}

/**
 * Format a string containing numbers with thousands separators.
 * This is a compatibility function for the existing formatNumberWithCommas pattern.
 * @param value - String potentially containing numbers
 */
export function formatNumberWithCommas(value: string): string {
  if (!value) return value;
  
  // Match numbers with 4+ digits in the string
  return value.replace(/\d{4,}/g, (match) => {
    return parseInt(match, 10).toLocaleString('en-US');
  });
}

/**
 * Get the react-calendar calendarType based on week start preference.
 * @param preferences - User display preferences (uses defaults if not provided)
 */
export function getCalendarType(
  preferences?: Partial<UserDisplayPreferences> | null
): 'gregory' | 'iso8601' {
  const prefs = { ...DEFAULT_DISPLAY_PREFERENCES, ...preferences };
  return prefs.weekStartsOn === 'monday' ? 'iso8601' : 'gregory';
}
